import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  RichTextContent,
  useContentToken,
} from '@vibesites/runtime/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Search, X } from 'lucide-react';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';

type FaqItemMeta = {
  id: string;
  categoryId: string;
  titleKey: string;
  qPath: string;
  aPath: string;
  qDefault: string;
  aDefault: string;
};

const FAQ_ITEMS: FaqItemMeta[] = [
  {
    id: 'about_1',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_1_q',
    aPath: 'faqscontent.about_1_a',
    qDefault: 'Who is American Landmark?',
    aDefault: 'American Landmark Apartments is a Tampa-based multifamily owner-operator committed to creating vibrant communities across the Sun Belt. We own and operate apartment communities with a vertically integrated platform that brings investment, operations, renovation, and property management together under one roof.',
  },
  {
    id: 'about_2',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_2_q',
    aPath: 'faqscontent.about_2_a',
    qDefault: 'How long has American Landmark been in business?',
    aDefault: 'American Landmark was founded in 2016, built on more than 25 years of multifamily expertise following the successful sale of Landmark Apartment Trust. Since then, we have grown into one of the nation\'s leading multifamily owner-operators.',
  },
  {
    id: 'about_3',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_3_q',
    aPath: 'faqscontent.about_3_a',
    qDefault: 'Where does American Landmark operate?',
    aDefault: 'We focus on high-growth Sun Belt markets and own and operate communities across states including Arizona, Florida, Georgia, North Carolina, South Carolina, Tennessee, and Texas.',
  },
  {
    id: 'about_4',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_4_q',
    aPath: 'faqscontent.about_4_a',
    qDefault: 'How many apartment communities does American Landmark own and manage?',
    aDefault: 'American Landmark owns and operates 35+ apartment communities representing approximately 36,000 apartment homes and more than $6 billion in gross assets under management.',
  },
  {
    id: 'about_5',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_5_q',
    aPath: 'faqscontent.about_5_a',
    qDefault: 'What makes American Landmark different from other multifamily companies?',
    aDefault: 'Our vertically integrated platform is our differentiator. Investment, construction and design, property management, portfolio management, revenue management, and capital raising all sit under one roof — giving us greater control, accountability, and alignment from acquisition through everyday resident service.',
  },
  {
    id: 'about_6',
    categoryId: 'about',
    titleKey: 'cat_about',
    qPath: 'faqscontent.about_6_q',
    aPath: 'faqscontent.about_6_a',
    qDefault: 'What does American Landmark mean by resident-first service?',
    aDefault: 'Resident-first service means every operational decision starts with the people who call our communities home. Through Landmark360® — our resident promise — we deliver dependable service, clear communication, exclusive benefits, and experiences designed to make apartment living simpler and more enjoyable.',
  },
  {
    id: 'landmark360_1',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_1_q',
    aPath: 'faqscontent.landmark360_1_a',
    qDefault: 'What is Landmark360®?',
    aDefault: 'Landmark360® is American Landmark\'s resident promise — a collection of exclusive benefits, service guarantees, and experiences designed to make apartment living simpler, more flexible, and more enjoyable from move-in through renewal.',
  },
  {
    id: 'landmark360_2',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_2_q',
    aPath: 'faqscontent.landmark360_2_a',
    qDefault: 'What benefits does Landmark360® include?',
    aDefault: 'Landmark360® includes easier everyday living through online payments and maintenance requests, community experiences like resident events and wellness activities, pet-friendly living, and flexible solutions when life changes. It is backed by the Landmark360® Promise: Move-In Satisfaction, a 48-Hour Completion Promise for routine service, and a Hassle-Free Relocation Promise.',
  },
  {
    id: 'landmark360_3',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_3_q',
    aPath: 'faqscontent.landmark360_3_a',
    qDefault: 'What is the Move-In Satisfaction Promise?',
    aDefault: 'American Landmark rolls out the red carpet at move-in with a reserved parking space for your moving vehicle, a welcome home gift, and a 100% satisfaction guarantee in the first two weeks. If you are not content with your new space within those two weeks, we will refund your security deposit and, if applicable, pet deposit (minus any damages) and waive lease-termination fees. You must provide a written 15-day notice to vacate at least 15 days before moving, and rent charges must be paid in full through the move-out date. Free rent concessions cannot be applied toward those rent obligations.',
  },
  {
    id: 'landmark360_4',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_4_q',
    aPath: 'faqscontent.landmark360_4_a',
    qDefault: 'What is the 48-Hour Completion Promise?',
    aDefault: 'We guarantee a 48-hour response and completion time for routine service requests. If we fail to meet this standard, you will be compensated for the inconvenience. Our dedicated team is always on standby to help keep your home in great condition.',
  },
  {
    id: 'landmark360_5',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_5_q',
    aPath: 'faqscontent.landmark360_5_a',
    qDefault: 'What is the Hassle-Free Relocation Promise?',
    aDefault: 'Need to move for work or personal reasons? As a Landmark360® resident, you can transfer to any other American Landmark community across the country without paying standard lease break fees. We make the transition seamless so you can focus on your next chapter.',
  },
  {
    id: 'landmark360_6',
    categoryId: 'landmark360',
    titleKey: 'cat_landmark360',
    qPath: 'faqscontent.landmark360_6_q',
    aPath: 'faqscontent.landmark360_6_a',
    qDefault: 'How can I learn more about Landmark360®?',
    aDefault: 'Visit our Landmark360® page to explore the full resident promise, or contact us if you have questions about benefits at a specific community.',
  },
  {
    id: 'residents_1',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_1_q',
    aPath: 'faqscontent.residents_1_a',
    qDefault: 'Where can I search available apartments?',
    aDefault: 'Browse American Landmark communities on our Our Communities page. You can filter by state and city, explore community details, and visit individual property websites to view floor plans and availability.',
  },
  {
    id: 'residents_2',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_2_q',
    aPath: 'faqscontent.residents_2_a',
    qDefault: 'How do I schedule a tour?',
    aDefault: 'Visit the community you are interested in from Our Communities , then use that property\'s website to schedule a tour online or contact the on-site leasing team directly.',
  },
  {
    id: 'residents_3',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_3_q',
    aPath: 'faqscontent.residents_3_a',
    qDefault: 'How do I apply for an apartment?',
    aDefault: 'Applications are submitted through the individual community website once you have selected a floor plan. Each community\'s leasing team can walk you through eligibility, required documents, and next steps.',
  },
  {
    id: 'residents_4',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_4_q',
    aPath: 'faqscontent.residents_4_a',
    qDefault: 'How do I submit a maintenance request?',
    aDefault: 'Current residents can submit maintenance requests through the Resident Portal , or by contacting their community\'s management office. For emergencies, follow the after-hours instructions provided by your community.',
  },
  {
    id: 'residents_5',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_5_q',
    aPath: 'faqscontent.residents_5_a',
    qDefault: 'Are American Landmark apartments pet-friendly?',
    aDefault: 'Many American Landmark communities welcome pets, though policies, pet fees, and breed or weight restrictions vary by property. Check the community website or speak with the on-site team for that location\'s current pet policy.',
  },
  {
    id: 'residents_6',
    categoryId: 'residents',
    titleKey: 'cat_residents',
    qPath: 'faqscontent.residents_6_q',
    aPath: 'faqscontent.residents_6_a',
    qDefault: 'How can I contact my community\'s management team?',
    aDefault: 'Find your community on Our Communities for on-site contact details, or log in to the Resident Portal for resident-specific support and messaging.',
  },
  {
    id: 'careers_1',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_1_q',
    aPath: 'faqscontent.careers_1_a',
    qDefault: 'What is it like to work at American Landmark?',
    aDefault: 'Our culture is built on teamwork, trust, and continuous improvement. Associates work alongside passionate professionals who care about residents, each other, and the communities we serve — with opportunities to learn, grow, and make a meaningful impact.',
  },
  {
    id: 'careers_2',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_2_q',
    aPath: 'faqscontent.careers_2_a',
    qDefault: 'What types of careers are available at American Landmark?',
    aDefault: 'We hire across property management, leasing, maintenance, corporate support, investments, construction, marketing, finance, and more. Explore open roles on our Careers page.',
  },
  {
    id: 'careers_3',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_3_q',
    aPath: 'faqscontent.careers_3_a',
    qDefault: 'Does American Landmark offer internships or early-career opportunities?',
    aDefault: 'Yes. Our paid Property Management Internship Program is designed for students and early-career professionals, with hands-on experience, mentorship, and a pathway into full-time multifamily roles.',
  },
  {
    id: 'careers_4',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_4_q',
    aPath: 'faqscontent.careers_4_a',
    qDefault: 'What benefits does American Landmark offer its team members?',
    aDefault: 'Full-time associates receive comprehensive medical, dental, and vision coverage, a 401(k) with company matching, generous paid time off, a housing discount at eligible American Landmark communities, and associate perks across travel, entertainment, and technology.',
  },
  {
    id: 'careers_5',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_5_q',
    aPath: 'faqscontent.careers_5_a',
    qDefault: 'How do I apply for a position at American Landmark?',
    aDefault: 'Browse open positions on our Careers page and apply online. Each listing includes the role description, qualifications, and location. Our recruiting team reviews applications and contacts qualified candidates to schedule interviews.',
  },
  {
    id: 'careers_6',
    categoryId: 'careers',
    titleKey: 'cat_careers',
    qPath: 'faqscontent.careers_6_q',
    aPath: 'faqscontent.careers_6_a',
    qDefault: 'Does American Landmark offer opportunities across multiple markets?',
    aDefault: 'Yes. With communities across the Sun Belt and a corporate presence in Tampa, we offer on-site and corporate opportunities across multiple markets. Each job listing specifies the location and whether the role is on-site, hybrid, or remote.',
  },
  {
    id: 'investors_1',
    categoryId: 'investors',
    titleKey: 'cat_investors',
    qPath: 'faqscontent.investors_1_q',
    aPath: 'faqscontent.investors_1_a',
    qDefault: 'Who invests with American Landmark?',
    aDefault: 'We partner with institutional investors, family offices, and capital partners seeking durable multifamily returns through a disciplined, operator-led investment platform.',
  },
  {
    id: 'investors_2',
    categoryId: 'investors',
    titleKey: 'cat_investors',
    qPath: 'faqscontent.investors_2_q',
    aPath: 'faqscontent.investors_2_a',
    qDefault: 'Where does American Landmark invest?',
    aDefault: 'We invest in mid-priced, value-add multifamily communities in high-growth Sun Belt markets supported by population migration, employment growth, affordability advantages, and long-term housing demand.',
  },
  {
    id: 'investors_3',
    categoryId: 'investors',
    titleKey: 'cat_investors',
    qPath: 'faqscontent.investors_3_q',
    aPath: 'faqscontent.investors_3_a',
    qDefault: 'What is American Landmark\'s investment strategy?',
    aDefault: 'Our strategy centers on acquiring well-located multifamily assets and creating value through renovation, revenue management, and hands-on operations — pairing long-horizon capital with a vertically integrated operating platform.',
  },
  {
    id: 'investors_4',
    categoryId: 'investors',
    titleKey: 'cat_investors',
    qPath: 'faqscontent.investors_4_q',
    aPath: 'faqscontent.investors_4_a',
    qDefault: 'What makes the Sun Belt attractive to American Landmark?',
    aDefault: 'Sun Belt markets benefit from ongoing migration, relative affordability, favorable climates, and economic growth — fundamentals that support durable multifamily demand and long-term investment performance.',
  },
  {
    id: 'investors_5',
    categoryId: 'investors',
    titleKey: 'cat_investors',
    qPath: 'faqscontent.investors_5_q',
    aPath: 'faqscontent.investors_5_a',
    qDefault: 'How can I learn more about American Landmark\'s investment opportunities?',
    aDefault: 'Visit our Investors page to learn about our platform and strategy, or use Investor Login for partner access. You can also contact us to connect with our capital markets team.',
  },
  {
    id: 'impact_1',
    categoryId: 'impact',
    titleKey: 'cat_impact',
    qPath: 'faqscontent.impact_1_q',
    aPath: 'faqscontent.impact_1_a',
    qDefault: 'How does American Landmark give back to the communities it serves?',
    aDefault: 'We invest in people and places through resident wellness programs, community engagement, philanthropy, housing accessibility initiatives, and signature programs like Artist in Residence and Educator in Residence. Learn more on our Our Impact page.',
  },
  {
    id: 'impact_2',
    categoryId: 'impact',
    titleKey: 'cat_impact',
    qPath: 'faqscontent.impact_2_q',
    aPath: 'faqscontent.impact_2_a',
    qDefault: 'What is American Landmark\'s approach to corporate responsibility?',
    aDefault: 'Our corporate responsibility approach is grounded in ESG — environmental stewardship, social responsibility, and strong governance — working together to support residents, associates, partners, and the neighborhoods where we operate.',
  },
  {
    id: 'impact_3',
    categoryId: 'impact',
    titleKey: 'cat_impact',
    qPath: 'faqscontent.impact_3_q',
    aPath: 'faqscontent.impact_3_a',
    qDefault: 'What is the Artists in Residence program?',
    aDefault: 'Artist in Residence is an American Landmark program that hosts artists within our communities, bringing creative energy, programming, and cultural experiences into the places residents call home.',
  },
  {
    id: 'impact_4',
    categoryId: 'impact',
    titleKey: 'cat_impact',
    qPath: 'faqscontent.impact_4_q',
    aPath: 'faqscontent.impact_4_a',
    qDefault: 'How does the Artists in Residence program benefit residents and communities?',
    aDefault: 'The program enriches everyday living by connecting residents with art, events, and creative collaboration — strengthening community culture while supporting artists who help make our neighborhoods more vibrant places to live.',
  },
  {
    id: 'partners_1',
    categoryId: 'partners',
    titleKey: 'cat_partners',
    qPath: 'faqscontent.partners_1_q',
    aPath: 'faqscontent.partners_1_a',
    qDefault: 'How can I become an American Landmark vendor or business partner?',
    aDefault: 'We welcome qualified vendors and partners who share our standards for quality and service. Share your company details and capabilities through our Contact Us page, and the appropriate team will follow up.',
  },
  {
    id: 'partners_2',
    categoryId: 'partners',
    titleKey: 'cat_partners',
    qPath: 'faqscontent.partners_2_q',
    aPath: 'faqscontent.partners_2_a',
    qDefault: 'How can I contact American Landmark about a business opportunity?',
    aDefault: 'Reach out via Contact Us with a brief description of the opportunity. Our team will route your inquiry to the right department — whether investments, operations, or corporate partnerships.',
  },
  {
    id: 'partners_3',
    categoryId: 'partners',
    titleKey: 'cat_partners',
    qPath: 'faqscontent.partners_3_q',
    aPath: 'faqscontent.partners_3_a',
    qDefault: 'Who should I contact about a property management partnership?',
    aDefault: 'For property management partnership discussions, contact us through our Contact Us page and note that your inquiry relates to property management. Our operations leadership will review and respond.',
  },
  {
    id: 'partners_4',
    categoryId: 'partners',
    titleKey: 'cat_partners',
    qPath: 'faqscontent.partners_4_q',
    aPath: 'faqscontent.partners_4_a',
    qDefault: 'How can I contact American Landmark about a media inquiry?',
    aDefault: 'Media inquiries can be submitted through Contact Us . Please include your outlet, deadline, and topic so our marketing and communications team can respond promptly.',
  },
];

const CATEGORY_ORDER = [
  'cat_landmark360',
  'cat_about',
  'cat_residents',
  'cat_careers',
  'cat_investors',
  'cat_impact',
  'cat_partners',
] as const;

const CATEGORY_TITLES: Record<(typeof CATEGORY_ORDER)[number], string> = {
  cat_about: 'About American Landmark',
  cat_landmark360: 'Landmark360®',
  cat_residents: 'For Residents & Renters',
  cat_careers: 'Careers',
  cat_investors: 'Investors & Capital Markets',
  cat_impact: 'Community Impact & Corporate Responsibility',
  cat_partners: 'Partners, Vendors & Business Inquiries',
};

const CATEGORY_PATHS: Record<(typeof CATEGORY_ORDER)[number], string> = {
  cat_about: 'faqscontent.cat_about',
  cat_landmark360: 'faqscontent.cat_landmark360',
  cat_residents: 'faqscontent.cat_residents',
  cat_careers: 'faqscontent.cat_careers',
  cat_investors: 'faqscontent.cat_investors',
  cat_impact: 'faqscontent.cat_impact',
  cat_partners: 'faqscontent.cat_partners',
};

function FaqAccordionItem({
  item,
  open,
  onToggle,
}: {
  item: FaqItemMeta;
  open: boolean;
  onToggle: () => void;
}) {
  const faqQClass =
    'font-heading text-[clamp(18px,1.8vw,24px)] font-semibold text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]';
  const faqAClass = 'pb-6 font-heading text-base font-normal leading-6 text-[rgb(var(--ala-text))]/80 [&_a]:text-[rgb(var(--ala-secondary))] [&_a]:underline-offset-2 hover:[&_a]:underline';

  return (
    <div className="border-b border-[rgb(var(--ala-text))]/15">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-6 text-left"
      >
        <RichTextContent path={item.qPath} label={item.qDefault} as="span" className={faqQClass} />
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-[rgb(var(--ala-text-muted))] transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <RichTextContent path={item.aPath} label={`Answer: ${item.qDefault}`} as="div" className={faqAClass} />
          </motion.div>
        ) : (
          <span className="sr-only">
            <RichTextContent path={item.aPath} label={`Answer: ${item.qDefault}`} as="span" />
          </span>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FaqsContent() {
  const [query, setQuery] = useState('');
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const searchPlaceholder =
    plainText(useContentToken('faqscontent.search_placeholder')) || 'Search questions and answers';

  const normalizedQuery = query.trim().toLowerCase();

  const visibleItems = useMemo(() => {
    if (!normalizedQuery) return FAQ_ITEMS;
    return FAQ_ITEMS.filter((item) => {
      const categoryTitle =
        CATEGORY_TITLES[item.titleKey as (typeof CATEGORY_ORDER)[number]] || '';
      const haystack = `${categoryTitle} ${item.qDefault} ${item.aDefault}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [normalizedQuery]);

  const grouped = useMemo(() => {
    const map = new Map<(typeof CATEGORY_ORDER)[number], FaqItemMeta[]>();
    for (const key of CATEGORY_ORDER) map.set(key, []);
    for (const item of visibleItems) {
      const key = item.titleKey as (typeof CATEGORY_ORDER)[number];
      const list = map.get(key);
      if (list) list.push(item);
    }
    return CATEGORY_ORDER.map((titleKey) => ({
      titleKey,
      titlePath: CATEGORY_PATHS[titleKey],
      items: map.get(titleKey) || [],
    })).filter((group) => group.items.length > 0);
  }, [visibleItems]);

  const faqJsonKey = FAQ_ITEMS.map((item) => `${item.qDefault}|${item.aDefault}`).join('||');

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.qDefault,
        acceptedAnswer: { '@type': 'Answer', text: item.aDefault },
      })),
    });
    script.setAttribute('data-faq-jsonld', 'faqs-page');
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqJsonKey]);

  useEffect(() => {
    if (openFaq && !visibleItems.some((item) => item.id === openFaq)) {
      setOpenFaq(null);
    }
  }, [openFaq, visibleItems]);

  return (
    <>
      <section className="w-full bg-[rgb(var(--ala-background))] px-6 pb-10 pt-16 md:px-[clamp(16px,6vw,87px)] md:pb-12 md:pt-24">
        <div className="mx-auto max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col gap-8"
          >
            <RichTextContent
              path="faqscontent.intro"
              label="Intro"
              as="p"
              className="font-heading text-[clamp(20px,2.2vw,28px)] font-medium leading-[1.35] tracking-[-0.02em] text-[rgb(var(--ala-text))]"
            />

            <label className="relative block w-full">
              <span className="sr-only">Search FAQs</span>
              <Search
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[rgb(var(--ala-text-muted))]"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-xl border border-[rgb(var(--ala-text))]/15 bg-[rgb(var(--ala-surface))] py-4 pl-12 pr-12 font-heading text-base text-[rgb(var(--ala-text))] outline-none transition placeholder:text-[rgb(var(--ala-text-muted))] focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20"
              />
              <span className="hidden">
                <EditableToken path="faqscontent.search_placeholder" type="text" label="Search Placeholder" as="span">
                  {searchPlaceholder}
                </EditableToken>
              </span>
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[rgb(var(--ala-text-muted))] transition hover:bg-[rgb(var(--ala-text))]/5 hover:text-[rgb(var(--ala-text))]"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </label>
          </motion.div>
        </div>
      </section>

      <section className="w-full bg-[rgb(var(--ala-surface))] px-6 py-16 md:px-[clamp(16px,6vw,87px)] md:py-24">
        <div className="mx-auto max-w-[900px]">
          {grouped.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-[rgb(var(--ala-text))]/10 bg-[rgb(var(--ala-background))] px-6 py-10 text-center"
            >
              <RichTextContent
                path="faqscontent.empty_message"
                label="No Results Message"
                as="p"
                className="font-heading text-base leading-relaxed text-[rgb(var(--ala-text-muted))]"
              />
            </motion.div>
          ) : (
            <div className="flex flex-col gap-14">
              {grouped.map((group, groupIndex) => (
                <motion.div
                  key={group.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={alaViewport}
                  transition={{ delay: Math.min(groupIndex * 0.05, 0.2) }}
                >
                  <div className="mb-6 flex items-center gap-3">
                    <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
                    <RichTextContent
                      path={group.titlePath}
                      label={CATEGORY_TITLES[group.titleKey]}
                      as="h2"
                      className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-text))]"
                    />
                  </div>
                  <div className="w-full">
                    {group.items.map((item) => (
                      <FaqAccordionItem
                        key={item.id}
                        item={item}
                        open={openFaq === item.id}
                        onToggle={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                      />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Static schema anchors for dynamic FAQ paths */}
      <div className="hidden" aria-hidden>
        <RichTextContent path="faqscontent.cat_about" label="About American Landmark Category" as="span" />
        <RichTextContent path="faqscontent.about_1_q" label="Who is American Landmark?" as="span" />
        <RichTextContent path="faqscontent.about_1_a" label="Answer: Who is American Landmark?" as="span" />
        <RichTextContent path="faqscontent.about_2_q" label="How long has American Landmark been in business?" as="span" />
        <RichTextContent path="faqscontent.about_2_a" label="Answer: How long has American Landmark been in business?" as="span" />
        <RichTextContent path="faqscontent.about_3_q" label="Where does American Landmark operate?" as="span" />
        <RichTextContent path="faqscontent.about_3_a" label="Answer: Where does American Landmark operate?" as="span" />
        <RichTextContent path="faqscontent.about_4_q" label="How many apartment communities does American Landmark own and manage?" as="span" />
        <RichTextContent path="faqscontent.about_4_a" label="Answer: How many apartment communities does American Landmark own and manage?" as="span" />
        <RichTextContent path="faqscontent.about_5_q" label="What makes American Landmark different from other multifamily companies?" as="span" />
        <RichTextContent path="faqscontent.about_5_a" label="Answer: What makes American Landmark different from other multifamily companies?" as="span" />
        <RichTextContent path="faqscontent.about_6_q" label="What does American Landmark mean by resident-first service?" as="span" />
        <RichTextContent path="faqscontent.about_6_a" label="Answer: What does American Landmark mean by resident-first service?" as="span" />
        <RichTextContent path="faqscontent.cat_landmark360" label="Landmark360 Category" as="span" />
        <RichTextContent path="faqscontent.landmark360_1_q" label="What is Landmark360®?" as="span" />
        <RichTextContent path="faqscontent.landmark360_1_a" label="Answer: What is Landmark360®?" as="span" />
        <RichTextContent path="faqscontent.landmark360_2_q" label="What benefits does Landmark360® include?" as="span" />
        <RichTextContent path="faqscontent.landmark360_2_a" label="Answer: What benefits does Landmark360® include?" as="span" />
        <RichTextContent path="faqscontent.landmark360_3_q" label="What is the Move-In Satisfaction Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_3_a" label="Answer: What is the Move-In Satisfaction Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_4_q" label="What is the 48-Hour Completion Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_4_a" label="Answer: What is the 48-Hour Completion Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_5_q" label="What is the Hassle-Free Relocation Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_5_a" label="Answer: What is the Hassle-Free Relocation Promise?" as="span" />
        <RichTextContent path="faqscontent.landmark360_6_q" label="How can I learn more about Landmark360®?" as="span" />
        <RichTextContent path="faqscontent.landmark360_6_a" label="Answer: How can I learn more about Landmark360®?" as="span" />
        <RichTextContent path="faqscontent.cat_residents" label="For Residents & Renters Category" as="span" />
        <RichTextContent path="faqscontent.residents_1_q" label="Where can I search available apartments?" as="span" />
        <RichTextContent path="faqscontent.residents_1_a" label="Answer: Where can I search available apartments?" as="span" />
        <RichTextContent path="faqscontent.residents_2_q" label="How do I schedule a tour?" as="span" />
        <RichTextContent path="faqscontent.residents_2_a" label="Answer: How do I schedule a tour?" as="span" />
        <RichTextContent path="faqscontent.residents_3_q" label="How do I apply for an apartment?" as="span" />
        <RichTextContent path="faqscontent.residents_3_a" label="Answer: How do I apply for an apartment?" as="span" />
        <RichTextContent path="faqscontent.residents_4_q" label="How do I submit a maintenance request?" as="span" />
        <RichTextContent path="faqscontent.residents_4_a" label="Answer: How do I submit a maintenance request?" as="span" />
        <RichTextContent path="faqscontent.residents_5_q" label="Are American Landmark apartments pet-friendly?" as="span" />
        <RichTextContent path="faqscontent.residents_5_a" label="Answer: Are American Landmark apartments pet-friendly?" as="span" />
        <RichTextContent path="faqscontent.residents_6_q" label="How can I contact my community\'s management team?" as="span" />
        <RichTextContent path="faqscontent.residents_6_a" label="Answer: How can I contact my community\'s management team?" as="span" />
        <RichTextContent path="faqscontent.cat_careers" label="Careers Category" as="span" />
        <RichTextContent path="faqscontent.careers_1_q" label="What is it like to work at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_1_a" label="Answer: What is it like to work at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_2_q" label="What types of careers are available at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_2_a" label="Answer: What types of careers are available at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_3_q" label="Does American Landmark offer internships or early-career opportunities?" as="span" />
        <RichTextContent path="faqscontent.careers_3_a" label="Answer: Does American Landmark offer internships or early-career opportunities?" as="span" />
        <RichTextContent path="faqscontent.careers_4_q" label="What benefits does American Landmark offer its team members?" as="span" />
        <RichTextContent path="faqscontent.careers_4_a" label="Answer: What benefits does American Landmark offer its team members?" as="span" />
        <RichTextContent path="faqscontent.careers_5_q" label="How do I apply for a position at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_5_a" label="Answer: How do I apply for a position at American Landmark?" as="span" />
        <RichTextContent path="faqscontent.careers_6_q" label="Does American Landmark offer opportunities across multiple markets?" as="span" />
        <RichTextContent path="faqscontent.careers_6_a" label="Answer: Does American Landmark offer opportunities across multiple markets?" as="span" />
        <RichTextContent path="faqscontent.cat_investors" label="Investors & Capital Markets Category" as="span" />
        <RichTextContent path="faqscontent.investors_1_q" label="Who invests with American Landmark?" as="span" />
        <RichTextContent path="faqscontent.investors_1_a" label="Answer: Who invests with American Landmark?" as="span" />
        <RichTextContent path="faqscontent.investors_2_q" label="Where does American Landmark invest?" as="span" />
        <RichTextContent path="faqscontent.investors_2_a" label="Answer: Where does American Landmark invest?" as="span" />
        <RichTextContent path="faqscontent.investors_3_q" label="What is American Landmark\'s investment strategy?" as="span" />
        <RichTextContent path="faqscontent.investors_3_a" label="Answer: What is American Landmark\'s investment strategy?" as="span" />
        <RichTextContent path="faqscontent.investors_4_q" label="What makes the Sun Belt attractive to American Landmark?" as="span" />
        <RichTextContent path="faqscontent.investors_4_a" label="Answer: What makes the Sun Belt attractive to American Landmark?" as="span" />
        <RichTextContent path="faqscontent.investors_5_q" label="How can I learn more about American Landmark\'s investment opportunities?" as="span" />
        <RichTextContent path="faqscontent.investors_5_a" label="Answer: How can I learn more about American Landmark\'s investment opportunities?" as="span" />
        <RichTextContent path="faqscontent.cat_impact" label="Community Impact & Corporate Responsibility Category" as="span" />
        <RichTextContent path="faqscontent.impact_1_q" label="How does American Landmark give back to the communities it serves?" as="span" />
        <RichTextContent path="faqscontent.impact_1_a" label="Answer: How does American Landmark give back to the communities it serves?" as="span" />
        <RichTextContent path="faqscontent.impact_2_q" label="What is American Landmark\'s approach to corporate responsibility?" as="span" />
        <RichTextContent path="faqscontent.impact_2_a" label="Answer: What is American Landmark\'s approach to corporate responsibility?" as="span" />
        <RichTextContent path="faqscontent.impact_3_q" label="What is the Artists in Residence program?" as="span" />
        <RichTextContent path="faqscontent.impact_3_a" label="Answer: What is the Artists in Residence program?" as="span" />
        <RichTextContent path="faqscontent.impact_4_q" label="How does the Artists in Residence program benefit residents and communities?" as="span" />
        <RichTextContent path="faqscontent.impact_4_a" label="Answer: How does the Artists in Residence program benefit residents and communities?" as="span" />
        <RichTextContent path="faqscontent.cat_partners" label="Partners, Vendors & Business Inquiries Category" as="span" />
        <RichTextContent path="faqscontent.partners_1_q" label="How can I become an American Landmark vendor or business partner?" as="span" />
        <RichTextContent path="faqscontent.partners_1_a" label="Answer: How can I become an American Landmark vendor or business partner?" as="span" />
        <RichTextContent path="faqscontent.partners_2_q" label="How can I contact American Landmark about a business opportunity?" as="span" />
        <RichTextContent path="faqscontent.partners_2_a" label="Answer: How can I contact American Landmark about a business opportunity?" as="span" />
        <RichTextContent path="faqscontent.partners_3_q" label="Who should I contact about a property management partnership?" as="span" />
        <RichTextContent path="faqscontent.partners_3_a" label="Answer: Who should I contact about a property management partnership?" as="span" />
        <RichTextContent path="faqscontent.partners_4_q" label="How can I contact American Landmark about a media inquiry?" as="span" />
        <RichTextContent path="faqscontent.partners_4_a" label="Answer: How can I contact American Landmark about a media inquiry?" as="span" />
        <RichTextContent path="faqscontent.empty_message" label="No Results Message" as="span" />
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqsContent',
  displayName: 'FAQs Content',
  tokens: {
    'faqscontent.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>Have a question? Find answers about American Landmark, Landmark360, our communities, careers, investments, and impact.</p>',
    },
    'faqscontent.search_placeholder': {
      label: 'Search Placeholder',
      type: 'text',
      defaultValue: 'Search questions and answers',
    },
    'faqscontent.empty_message': {
      label: 'No Results Message',
      type: 'richtext',
      defaultValue: '<p>No matching questions. Try a different search or browse the categories below.</p>',
    },
    'faqscontent.cat_about': {
      label: 'About American Landmark Category',
      type: 'richtext',
      defaultValue: '<p>About American Landmark</p>',
    },
    'faqscontent.about_1_q': {
      label: 'Who is American Landmark?',
      type: 'richtext',
      defaultValue: '<p>Who is American Landmark?</p>',
    },
    'faqscontent.about_1_a': {
      label: 'Answer: Who is American Landmark?',
      type: 'richtext',
      defaultValue: '<p>American Landmark Apartments is a Tampa-based multifamily owner-operator committed to creating vibrant communities across the Sun Belt. We own and operate apartment communities with a vertically integrated platform that brings investment, operations, renovation, and property management together under one roof.</p>',
    },
    'faqscontent.about_2_q': {
      label: 'How long has American Landmark been in business?',
      type: 'richtext',
      defaultValue: '<p>How long has American Landmark been in business?</p>',
    },
    'faqscontent.about_2_a': {
      label: 'Answer: How long has American Landmark been in business?',
      type: 'richtext',
      defaultValue: '<p>American Landmark was founded in 2016, built on more than 25 years of multifamily expertise following the successful sale of Landmark Apartment Trust. Since then, we have grown into one of the nation\'s leading multifamily owner-operators.</p>',
    },
    'faqscontent.about_3_q': {
      label: 'Where does American Landmark operate?',
      type: 'richtext',
      defaultValue: '<p>Where does American Landmark operate?</p>',
    },
    'faqscontent.about_3_a': {
      label: 'Answer: Where does American Landmark operate?',
      type: 'richtext',
      defaultValue: '<p>We focus on high-growth Sun Belt markets and own and operate communities across states including Arizona, Florida, Georgia, North Carolina, South Carolina, Tennessee, and Texas.</p>',
    },
    'faqscontent.about_4_q': {
      label: 'How many apartment communities does American Landmark own and manage?',
      type: 'richtext',
      defaultValue: '<p>How many apartment communities does American Landmark own and manage?</p>',
    },
    'faqscontent.about_4_a': {
      label: 'Answer: How many apartment communities does American Landmark own and manage?',
      type: 'richtext',
      defaultValue: '<p>American Landmark owns and operates 35+ apartment communities representing approximately 36,000 apartment homes and more than $6 billion in gross assets under management.</p>',
    },
    'faqscontent.about_5_q': {
      label: 'What makes American Landmark different from other multifamily companies?',
      type: 'richtext',
      defaultValue: '<p>What makes American Landmark different from other multifamily companies?</p>',
    },
    'faqscontent.about_5_a': {
      label: 'Answer: What makes American Landmark different from other multifamily companies?',
      type: 'richtext',
      defaultValue: '<p>Our vertically integrated platform is our differentiator. Investment, construction and design, property management, portfolio management, revenue management, and capital raising all sit under one roof — giving us greater control, accountability, and alignment from acquisition through everyday resident service.</p>',
    },
    'faqscontent.about_6_q': {
      label: 'What does American Landmark mean by resident-first service?',
      type: 'richtext',
      defaultValue: '<p>What does American Landmark mean by resident-first service?</p>',
    },
    'faqscontent.about_6_a': {
      label: 'Answer: What does American Landmark mean by resident-first service?',
      type: 'richtext',
      defaultValue: '<p>Resident-first service means every operational decision starts with the people who call our communities home. Through Landmark360® — our resident promise — we deliver dependable service, clear communication, exclusive benefits, and experiences designed to make apartment living simpler and more enjoyable.</p>',
    },
    'faqscontent.cat_landmark360': {
      label: 'Landmark360 Category',
      type: 'richtext',
      defaultValue: '<p>Landmark360®</p>',
    },
    'faqscontent.landmark360_1_q': {
      label: 'What is Landmark360®?',
      type: 'richtext',
      defaultValue: '<p>What is Landmark360®?</p>',
    },
    'faqscontent.landmark360_1_a': {
      label: 'Answer: What is Landmark360®?',
      type: 'richtext',
      defaultValue:
        '<p>Landmark360® is American Landmark\'s resident promise — a collection of exclusive benefits, service guarantees, and experiences designed to make apartment living simpler, more flexible, and more enjoyable from move-in through renewal.</p>',
    },
    'faqscontent.landmark360_2_q': {
      label: 'What benefits does Landmark360® include?',
      type: 'richtext',
      defaultValue: '<p>What benefits does Landmark360® include?</p>',
    },
    'faqscontent.landmark360_2_a': {
      label: 'Answer: What benefits does Landmark360® include?',
      type: 'richtext',
      defaultValue:
        '<p>Landmark360® includes easier everyday living through online payments and maintenance requests, community experiences like resident events and wellness activities, pet-friendly living, and flexible solutions when life changes. It is backed by the Landmark360® Promise: Move-In Satisfaction, a 48-Hour Completion Promise for routine service, and a Hassle-Free Relocation Promise.</p>',
    },
    'faqscontent.landmark360_3_q': {
      label: 'What is the Move-In Satisfaction Promise?',
      type: 'richtext',
      defaultValue: '<p>What is the Move-In Satisfaction Promise?</p>',
    },
    'faqscontent.landmark360_3_a': {
      label: 'Answer: What is the Move-In Satisfaction Promise?',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark rolls out the red carpet at move-in with a reserved parking space for your moving vehicle, a welcome home gift, and a 100% satisfaction guarantee in the first two weeks. If you are not content with your new space within those two weeks, we will refund your security deposit and, if applicable, pet deposit (minus any damages) and waive lease-termination fees. You must provide a written 15-day notice to vacate at least 15 days before moving, and rent charges must be paid in full through the move-out date. Free rent concessions cannot be applied toward those rent obligations.</p>',
    },
    'faqscontent.landmark360_4_q': {
      label: 'What is the 48-Hour Completion Promise?',
      type: 'richtext',
      defaultValue: '<p>What is the 48-Hour Completion Promise?</p>',
    },
    'faqscontent.landmark360_4_a': {
      label: 'Answer: What is the 48-Hour Completion Promise?',
      type: 'richtext',
      defaultValue:
        '<p>We guarantee a 48-hour response and completion time for routine service requests. If we fail to meet this standard, you will be compensated for the inconvenience. Our dedicated team is always on standby to help keep your home in great condition.</p>',
    },
    'faqscontent.landmark360_5_q': {
      label: 'What is the Hassle-Free Relocation Promise?',
      type: 'richtext',
      defaultValue: '<p>What is the Hassle-Free Relocation Promise?</p>',
    },
    'faqscontent.landmark360_5_a': {
      label: 'Answer: What is the Hassle-Free Relocation Promise?',
      type: 'richtext',
      defaultValue:
        '<p>Need to move for work or personal reasons? As a Landmark360® resident, you can transfer to any other American Landmark community across the country without paying standard lease break fees. We make the transition seamless so you can focus on your next chapter.</p>',
    },
    'faqscontent.landmark360_6_q': {
      label: 'How can I learn more about Landmark360®?',
      type: 'richtext',
      defaultValue: '<p>How can I learn more about Landmark360®?</p>',
    },
    'faqscontent.landmark360_6_a': {
      label: 'Answer: How can I learn more about Landmark360®?',
      type: 'richtext',
      defaultValue:
        '<p>Visit our <a href="/landmark360">Landmark360®</a> page to explore the full resident promise, or <a href="/contact-us">contact us</a> if you have questions about benefits at a specific community.</p>',
    },
    'faqscontent.cat_residents': {
      label: 'For Residents & Renters Category',
      type: 'richtext',
      defaultValue: '<p>For Residents & Renters</p>',
    },
    'faqscontent.residents_1_q': {
      label: 'Where can I search available apartments?',
      type: 'richtext',
      defaultValue: '<p>Where can I search available apartments?</p>',
    },
    'faqscontent.residents_1_a': {
      label: 'Answer: Where can I search available apartments?',
      type: 'richtext',
      defaultValue: '<p>Browse American Landmark communities on our <a href="/our-communities">Our Communities</a> page. You can filter by state and city, explore community details, and visit individual property websites to view floor plans and availability.</p>',
    },
    'faqscontent.residents_2_q': {
      label: 'How do I schedule a tour?',
      type: 'richtext',
      defaultValue: '<p>How do I schedule a tour?</p>',
    },
    'faqscontent.residents_2_a': {
      label: 'Answer: How do I schedule a tour?',
      type: 'richtext',
      defaultValue: '<p>Visit the community you are interested in from <a href="/our-communities">Our Communities</a>, then use that property\'s website to schedule a tour online or contact the on-site leasing team directly.</p>',
    },
    'faqscontent.residents_3_q': {
      label: 'How do I apply for an apartment?',
      type: 'richtext',
      defaultValue: '<p>How do I apply for an apartment?</p>',
    },
    'faqscontent.residents_3_a': {
      label: 'Answer: How do I apply for an apartment?',
      type: 'richtext',
      defaultValue: '<p>Applications are submitted through the individual community website once you have selected a floor plan. Each community\'s leasing team can walk you through eligibility, required documents, and next steps.</p>',
    },
    'faqscontent.residents_4_q': {
      label: 'How do I submit a maintenance request?',
      type: 'richtext',
      defaultValue: '<p>How do I submit a maintenance request?</p>',
    },
    'faqscontent.residents_4_a': {
      label: 'Answer: How do I submit a maintenance request?',
      type: 'richtext',
      defaultValue: '<p>Current residents can submit maintenance requests through the <a href="https://www.alapts.com/resident-portal" target="_blank" rel="noopener noreferrer">Resident Portal</a>, or by contacting their community\'s management office. For emergencies, follow the after-hours instructions provided by your community.</p>',
    },
    'faqscontent.residents_5_q': {
      label: 'Are American Landmark apartments pet-friendly?',
      type: 'richtext',
      defaultValue: '<p>Are American Landmark apartments pet-friendly?</p>',
    },
    'faqscontent.residents_5_a': {
      label: 'Answer: Are American Landmark apartments pet-friendly?',
      type: 'richtext',
      defaultValue: '<p>Many American Landmark communities welcome pets, though policies, pet fees, and breed or weight restrictions vary by property. Check the community website or speak with the on-site team for that location\'s current pet policy.</p>',
    },
    'faqscontent.residents_6_q': {
      label: 'How can I contact my community\'s management team?',
      type: 'richtext',
      defaultValue: '<p>How can I contact my community\'s management team?</p>',
    },
    'faqscontent.residents_6_a': {
      label: 'Answer: How can I contact my community\'s management team?',
      type: 'richtext',
      defaultValue: '<p>Find your community on <a href="/our-communities">Our Communities</a> for on-site contact details, or log in to the <a href="https://www.alapts.com/resident-portal" target="_blank" rel="noopener noreferrer">Resident Portal</a> for resident-specific support and messaging.</p>',
    },
    'faqscontent.cat_careers': {
      label: 'Careers Category',
      type: 'richtext',
      defaultValue: '<p>Careers</p>',
    },
    'faqscontent.careers_1_q': {
      label: 'What is it like to work at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>What is it like to work at American Landmark?</p>',
    },
    'faqscontent.careers_1_a': {
      label: 'Answer: What is it like to work at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>Our culture is built on teamwork, trust, and continuous improvement. Associates work alongside passionate professionals who care about residents, each other, and the communities we serve — with opportunities to learn, grow, and make a meaningful impact.</p>',
    },
    'faqscontent.careers_2_q': {
      label: 'What types of careers are available at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>What types of careers are available at American Landmark?</p>',
    },
    'faqscontent.careers_2_a': {
      label: 'Answer: What types of careers are available at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>We hire across property management, leasing, maintenance, corporate support, investments, construction, marketing, finance, and more. Explore open roles on our <a href="/careers">Careers</a> page.</p>',
    },
    'faqscontent.careers_3_q': {
      label: 'Does American Landmark offer internships or early-career opportunities?',
      type: 'richtext',
      defaultValue: '<p>Does American Landmark offer internships or early-career opportunities?</p>',
    },
    'faqscontent.careers_3_a': {
      label: 'Answer: Does American Landmark offer internships or early-career opportunities?',
      type: 'richtext',
      defaultValue: '<p>Yes. Our paid Property Management Internship Program is designed for students and early-career professionals, with hands-on experience, mentorship, and a pathway into full-time multifamily roles.</p>',
    },
    'faqscontent.careers_4_q': {
      label: 'What benefits does American Landmark offer its team members?',
      type: 'richtext',
      defaultValue: '<p>What benefits does American Landmark offer its team members?</p>',
    },
    'faqscontent.careers_4_a': {
      label: 'Answer: What benefits does American Landmark offer its team members?',
      type: 'richtext',
      defaultValue: '<p>Full-time associates receive comprehensive medical, dental, and vision coverage, a 401(k) with company matching, generous paid time off, a housing discount at eligible American Landmark communities, and associate perks across travel, entertainment, and technology.</p>',
    },
    'faqscontent.careers_5_q': {
      label: 'How do I apply for a position at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>How do I apply for a position at American Landmark?</p>',
    },
    'faqscontent.careers_5_a': {
      label: 'Answer: How do I apply for a position at American Landmark?',
      type: 'richtext',
      defaultValue: '<p>Browse open positions on our <a href="/careers">Careers</a> page and apply online. Each listing includes the role description, qualifications, and location. Our recruiting team reviews applications and contacts qualified candidates to schedule interviews.</p>',
    },
    'faqscontent.careers_6_q': {
      label: 'Does American Landmark offer opportunities across multiple markets?',
      type: 'richtext',
      defaultValue: '<p>Does American Landmark offer opportunities across multiple markets?</p>',
    },
    'faqscontent.careers_6_a': {
      label: 'Answer: Does American Landmark offer opportunities across multiple markets?',
      type: 'richtext',
      defaultValue: '<p>Yes. With communities across the Sun Belt and a corporate presence in Tampa, we offer on-site and corporate opportunities across multiple markets. Each job listing specifies the location and whether the role is on-site, hybrid, or remote.</p>',
    },
    'faqscontent.cat_investors': {
      label: 'Investors & Capital Markets Category',
      type: 'richtext',
      defaultValue: '<p>Investors & Capital Markets</p>',
    },
    'faqscontent.investors_1_q': {
      label: 'Who invests with American Landmark?',
      type: 'richtext',
      defaultValue: '<p>Who invests with American Landmark?</p>',
    },
    'faqscontent.investors_1_a': {
      label: 'Answer: Who invests with American Landmark?',
      type: 'richtext',
      defaultValue: '<p>We partner with institutional investors, family offices, and capital partners seeking durable multifamily returns through a disciplined, operator-led investment platform.</p>',
    },
    'faqscontent.investors_2_q': {
      label: 'Where does American Landmark invest?',
      type: 'richtext',
      defaultValue: '<p>Where does American Landmark invest?</p>',
    },
    'faqscontent.investors_2_a': {
      label: 'Answer: Where does American Landmark invest?',
      type: 'richtext',
      defaultValue: '<p>We invest in mid-priced, value-add multifamily communities in high-growth Sun Belt markets supported by population migration, employment growth, affordability advantages, and long-term housing demand.</p>',
    },
    'faqscontent.investors_3_q': {
      label: 'What is American Landmark\'s investment strategy?',
      type: 'richtext',
      defaultValue: '<p>What is American Landmark\'s investment strategy?</p>',
    },
    'faqscontent.investors_3_a': {
      label: 'Answer: What is American Landmark\'s investment strategy?',
      type: 'richtext',
      defaultValue: '<p>Our strategy centers on acquiring well-located multifamily assets and creating value through renovation, revenue management, and hands-on operations — pairing long-horizon capital with a vertically integrated operating platform.</p>',
    },
    'faqscontent.investors_4_q': {
      label: 'What makes the Sun Belt attractive to American Landmark?',
      type: 'richtext',
      defaultValue: '<p>What makes the Sun Belt attractive to American Landmark?</p>',
    },
    'faqscontent.investors_4_a': {
      label: 'Answer: What makes the Sun Belt attractive to American Landmark?',
      type: 'richtext',
      defaultValue: '<p>Sun Belt markets benefit from ongoing migration, relative affordability, favorable climates, and economic growth — fundamentals that support durable multifamily demand and long-term investment performance.</p>',
    },
    'faqscontent.investors_5_q': {
      label: 'How can I learn more about American Landmark\'s investment opportunities?',
      type: 'richtext',
      defaultValue: '<p>How can I learn more about American Landmark\'s investment opportunities?</p>',
    },
    'faqscontent.investors_5_a': {
      label: 'Answer: How can I learn more about American Landmark\'s investment opportunities?',
      type: 'richtext',
      defaultValue: '<p>Visit our <a href="/investors">Investors</a> page to learn about our platform and strategy, or use Investor Login for partner access. You can also <a href="/contact-us">contact us</a> to connect with our capital markets team.</p>',
    },
    'faqscontent.cat_impact': {
      label: 'Community Impact & Corporate Responsibility Category',
      type: 'richtext',
      defaultValue: '<p>Community Impact & Corporate Responsibility</p>',
    },
    'faqscontent.impact_1_q': {
      label: 'How does American Landmark give back to the communities it serves?',
      type: 'richtext',
      defaultValue: '<p>How does American Landmark give back to the communities it serves?</p>',
    },
    'faqscontent.impact_1_a': {
      label: 'Answer: How does American Landmark give back to the communities it serves?',
      type: 'richtext',
      defaultValue: '<p>We invest in people and places through resident wellness programs, community engagement, philanthropy, housing accessibility initiatives, and signature programs like Artist in Residence and Educator in Residence. Learn more on our <a href="/our-impact">Our Impact</a> page.</p>',
    },
    'faqscontent.impact_2_q': {
      label: 'What is American Landmark\'s approach to corporate responsibility?',
      type: 'richtext',
      defaultValue: '<p>What is American Landmark\'s approach to corporate responsibility?</p>',
    },
    'faqscontent.impact_2_a': {
      label: 'Answer: What is American Landmark\'s approach to corporate responsibility?',
      type: 'richtext',
      defaultValue: '<p>Our corporate responsibility approach is grounded in ESG — environmental stewardship, social responsibility, and strong governance — working together to support residents, associates, partners, and the neighborhoods where we operate.</p>',
    },
    'faqscontent.impact_3_q': {
      label: 'What is the Artists in Residence program?',
      type: 'richtext',
      defaultValue: '<p>What is the Artists in Residence program?</p>',
    },
    'faqscontent.impact_3_a': {
      label: 'Answer: What is the Artists in Residence program?',
      type: 'richtext',
      defaultValue: '<p>Artist in Residence is an American Landmark program that hosts artists within our communities, bringing creative energy, programming, and cultural experiences into the places residents call home.</p>',
    },
    'faqscontent.impact_4_q': {
      label: 'How does the Artists in Residence program benefit residents and communities?',
      type: 'richtext',
      defaultValue: '<p>How does the Artists in Residence program benefit residents and communities?</p>',
    },
    'faqscontent.impact_4_a': {
      label: 'Answer: How does the Artists in Residence program benefit residents and communities?',
      type: 'richtext',
      defaultValue: '<p>The program enriches everyday living by connecting residents with art, events, and creative collaboration — strengthening community culture while supporting artists who help make our neighborhoods more vibrant places to live.</p>',
    },
    'faqscontent.cat_partners': {
      label: 'Partners, Vendors & Business Inquiries Category',
      type: 'richtext',
      defaultValue: '<p>Partners, Vendors & Business Inquiries</p>',
    },
    'faqscontent.partners_1_q': {
      label: 'How can I become an American Landmark vendor or business partner?',
      type: 'richtext',
      defaultValue: '<p>How can I become an American Landmark vendor or business partner?</p>',
    },
    'faqscontent.partners_1_a': {
      label: 'Answer: How can I become an American Landmark vendor or business partner?',
      type: 'richtext',
      defaultValue: '<p>We welcome qualified vendors and partners who share our standards for quality and service. Share your company details and capabilities through our <a href="/contact-us">Contact Us</a> page, and the appropriate team will follow up.</p>',
    },
    'faqscontent.partners_2_q': {
      label: 'How can I contact American Landmark about a business opportunity?',
      type: 'richtext',
      defaultValue: '<p>How can I contact American Landmark about a business opportunity?</p>',
    },
    'faqscontent.partners_2_a': {
      label: 'Answer: How can I contact American Landmark about a business opportunity?',
      type: 'richtext',
      defaultValue: '<p>Reach out via <a href="/contact-us">Contact Us</a> with a brief description of the opportunity. Our team will route your inquiry to the right department — whether investments, operations, or corporate partnerships.</p>',
    },
    'faqscontent.partners_3_q': {
      label: 'Who should I contact about a property management partnership?',
      type: 'richtext',
      defaultValue: '<p>Who should I contact about a property management partnership?</p>',
    },
    'faqscontent.partners_3_a': {
      label: 'Answer: Who should I contact about a property management partnership?',
      type: 'richtext',
      defaultValue: '<p>For property management partnership discussions, contact us through our <a href="/contact-us">Contact Us</a> page and note that your inquiry relates to property management. Our operations leadership will review and respond.</p>',
    },
    'faqscontent.partners_4_q': {
      label: 'How can I contact American Landmark about a media inquiry?',
      type: 'richtext',
      defaultValue: '<p>How can I contact American Landmark about a media inquiry?</p>',
    },
    'faqscontent.partners_4_a': {
      label: 'Answer: How can I contact American Landmark about a media inquiry?',
      type: 'richtext',
      defaultValue: '<p>Media inquiries can be submitted through <a href="/contact-us">Contact Us</a>. Please include your outlet, deadline, and topic so our marketing and communications team can respond promptly.</p>',
    },
  },
};

(FaqsContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqsContent;
